import React, { useEffect, useRef, useState } from "react";
import {
  webLoadingFalse,
  webNavigate,
} from "../../services/navigate.service";
import PlusIcon from "../../images/PlusIcon";
import EditIconV2 from "../../images/EditIconV2";
import DeleteIconV2 from "../../images/DeleteIconV2";
import { deleteTerm, loadTerm } from "../../services/term.service";
import CloseIcon from "../../images/CloseIcon";
import { useResponsive } from "../../hook/useResponsive";
import { showApiError } from "../../services/apiError";
import TagStatus from "../../components/TagStatus";
import { ConfirmSheet } from "../../components/mobile";
import {
  ListColumn,
  ListQuery,
  ListRowAction,
  ListScreen,
  ListScreenHandle,
  cells,
  t,
} from "../../components/list";
interface TermRow {
  id: string;
  termCode: string | null;
  termName: string;
  termDescription: string | null;
  termDay: number;
  isDefault: boolean | null;
  isActive?: boolean;
}

interface TermsResponse {
  count: number;
  data: TermRow[];
}
const Terms = () => {
  const preLanguage = localStorage.getItem("language");
  const [language, setLanguage] = useState<any>(null);
  const listRef = useRef<ListScreenHandle>(null);
  const { isTouchLayout } = useResponsive();
  const emptyState = {
    title:
      language && language?.["No Payment Terms Found"]
        ? language?.["No Payment Terms Found"]
        : "No Payment Terms Found",
    desc:
      language &&
        language?.["Add terms to define payment deadlines for your invoices."]
        ? language?.["Add terms to define payment deadlines for your invoices."]
        : "Add terms to define payment deadlines for your invoices.",
  };
  const [showDelete, setShowDelete] = useState<boolean>(false);
  const [idDelete, setIdDelete] = useState<string>("");
  const [nameDelete, setNameDelete] = useState<string>("");
  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  /** One page of terms, ordered by name (the only sort /api/terms/list takes). */
  const fetchTerms = async (query: ListQuery) => {
    webLoadingFalse();
    const response = await loadTerm({
      page: query.page,
      limit: query.limit,
      termName: "",
      sort: query.sortDir,
    });
    const value: TermsResponse = response.data;
    return { rows: value?.data ?? [], count: value?.count ?? 0 };
  };

  const goToTerm = (id: string) => webNavigate("/" + preLanguage + "/terms/" + id);

  const handleShowDelete = (row: TermRow) => {
    setShowDelete(true);
    setIdDelete(String(row.id));
    setNameDelete(row.termName ?? "");
  };

  const handleDeleteTerm = async () => {
    try {
      await deleteTerm(idDelete);
    } catch (error) {
      showApiError(error, { language });
    } finally {
      listRef.current?.reload();
      setShowDelete(false);
    }
  };

  /* ---------------- List kit configuration ---------------- */

  const columns: ListColumn<TermRow>[] = [
    {
      key: "termCode",
      label: "Code",
      width: 140,
      hideable: false,
      mobile: "subtitle",
      onClick: (r) => goToTerm(String(r.id)),
    },
    {
      key: "termName",
      label: "Name",
      width: 220,
      sortable: true,
      render: cells.primary((r) => r.termName),
      mobile: "title",
      onClick: (r) => goToTerm(String(r.id)),
    },
    { key: "termDescription", label: "Description", width: 300, mobile: "sheet" },
    {
      key: "termDay",
      label: "Days",
      width: 90,
      render: cells.text((r) => r.termDay),
      // `value` only feeds the phone subtitle ("NET30 · 30 days").
      value: (r, ctx) => `${r.termDay} ${t(ctx.language, "Days", "Days").toLowerCase()}`,
      mobile: "subtitle",
    },
    {
      key: "isDefault",
      label: "Default",
      width: 100,
      value: (r, ctx) => (r.isDefault ? t(ctx.language, "Yes", "Yes") : t(ctx.language, "No", "No")),
      mobile: "sheet",
    },
    {
      key: "status",
      label: "Status",
      width: 120,
      render: (r, ctx) => {
        const status = r.isActive === false ? "Not Active" : "Active";
        return <TagStatus value={status} label={t(ctx.language, status, status)} />;
      },
      mobile: "trailing",
    },
  ];

  const rowActions: ListRowAction<TermRow>[] = [
    {
      key: "edit",
      label: "Edit",
      icon: <EditIconV2 />,
      sheetIcon: "inventory",
      onRun: (r) => goToTerm(String(r.id)),
    },
    {
      key: "delete",
      label: "Delete",
      icon: <DeleteIconV2 />,
      sheetIcon: "close",
      danger: true,
      onRun: (r) => handleShowDelete(r),
    },
  ];

  return (
    <>
      <div
        className="h-full relative "
        style={{
          height: "100%",
          overflowY: isTouchLayout ? "auto" : undefined,
          paddingBottom: isTouchLayout ? "24px" : undefined,
          display: isTouchLayout ? undefined : "flex",
          flexDirection: isTouchLayout ? undefined : "column",
          minHeight: 0,
        }}
      >
        <div className="lg:flex items-center justify-between" style={{ flex: "none" }}>
          <p
            style={{ color: "#222525", fontSize: "24px" }}
            className="font-semibold font-inter"
          >
            {language && language?.["Payment Terms"]
              ? language?.["Payment Terms"]
              : "Payment Terms"}
          </p>
          <div
            className="cursor-pointer flex items-center justify-center"
            style={{
              gap: "8px",
              padding: "8px 24px 8px 16px",
              backgroundColor: "#0F7679",
              borderRadius: "30px",
              marginTop: isTouchLayout ? "20px" : "0px",
              width: "fit-content",
            }}
            onClick={() => {
              webNavigate("/" + preLanguage + "/terms/new");
            }}
          >
            <div style={{ width: '24px', height: '24px' }} className="flex items-center justify-center">
              <PlusIcon />
            </div>
            <p
              style={{ color: "#FFFFFF" }}
              className="text-[14px] leading-[130%] font-medium font-inter"
            >
              {language && language?.["Add payment terms"]
                ? language?.["Add payment terms"]
                : "Add payment terms"}
            </p>
          </div>
        </div>
        <ListScreen
          ref={listRef}
          language={language}
          columns={columns}
          fetchRows={fetchTerms}
          search={false}
          defaultSort={{ sortBy: "termName", sortDir: "ASC" }}
          rowActions={rowActions}
          emptyState={emptyState}
        />
      </div>
      {isTouchLayout ? (
        <ConfirmSheet
          open={showDelete}
          title={
            language && language?.["Delete payment term?"]
              ? language?.["Delete payment term?"]
              : "Delete payment term?"
          }
          message={
            <>
              {language &&
                language?.[
                "This payment term will be permanently deleted. Are you sure you want to continue?"
                ]
                ? language?.[
                "This payment term will be permanently deleted. Are you sure you want to continue?"
                ]
                : "This payment term will be permanently deleted. Are you sure you want to continue?"}
              {nameDelete && (
                <div style={{ marginTop: "4px", fontWeight: 500, color: "#222525" }}>
                  {nameDelete}
                </div>
              )}
            </>
          }
          confirmLabel={language && language?.["Delete"] ? language?.["Delete"] : "Delete"}
          cancelLabel={language && language?.["Cancel"] ? language?.["Cancel"] : "Cancel"}
          onConfirm={handleDeleteTerm}
          onCancel={() => setShowDelete(false)}
        />
      ) : (
        <div
          className={`absolute w-full h-full top-0 left-0 flex items-center justify-center px-4 ${showDelete ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"} transition-opacity duration-300 ease-in-out`}
          style={{
            backgroundColor: "#22252552",
            zIndex: "200",
            transition: "opacity 0.3s ease, visibility 0.3s ease",
            width: "100vw",
            height: "100vh",
            left: 0,
            top: 0,
          }}
        >
          <div
            className={`transform transition-transform duration-300 ease-in-out ${showDelete ? "translate-y-0" : "translate-y-4 opacity-0"}`}
            style={{
              padding: "24px",
              borderRadius: "24px",
              backgroundColor: "#FFFFFF",
              width: "530px",
              transition: "transform 0.3s ease, opacity 0.3s ease",
            }}
          >
            <div className="flex items-center justify-between">
              <p
                style={{ fontSize: "20px", color: "#222525" }}
                className="font-semibold font-inter leading-[24px]"
              >
                {language && language?.["Delete payment term?"]
                  ? language?.["Delete payment term?"]
                  : "Delete payment term?"}
              </p>
              <div
                onClick={() => setShowDelete(false)}
                className="flex items-center justify-center cursor-pointer"
                style={{
                  width: "24px",
                  height: "24px",
                }}
              >
                <CloseIcon />
              </div>
            </div>
            <div
              className="flex items-center justify-between"
              style={{ marginTop: "12px" }}
            >
              <p
                className="text-[14px]  font-inter leading-[24px]"
                style={{ color: "#6A7878" }}
              >
                {language &&
                  language?.[
                  "This payment term will be permanently deleted. Are you sure you want to continue?"
                  ]
                  ? language?.[
                  "This payment term will be permanently deleted. Are you sure you want to continue?"
                  ]
                  : "This payment term will be permanently deleted. Are you sure you want to continue?"}
              </p>
              {/* <span className="text-main">[sale ID]</span> sale. */}
            </div>
            <div
              className="flex items-center justify-end"
              style={{ marginTop: "24px", gap: "12px" }}
            >
              <div
                onClick={() => setShowDelete(false)}
                className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
                style={{
                  borderRadius: "40px",
                  padding: "10px 24px",
                  border: "1px solid #C9D7D8",
                  color: "#222525",
                }}
              >
                {language && language?.["Cancel"]
                  ? language?.["Cancel"]
                  : "Cancel"}
              </div>
              <div
                onClick={handleDeleteTerm}
                className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
                style={{
                  borderRadius: "40px",
                  padding: "10px 24px",
                  backgroundColor: "#BA0E14",
                  color: "#FFFFFF",
                }}
              >
                {language && language?.["Delete"]
                  ? language?.["Delete"]
                  : "Delete"}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Terms;
